/**
 * 路径排除匹配工具
 *
 * 支持 Ant 风格通配符:
 * - `**` 匹配任意层级路径
 * - `*` 匹配单层路径中的任意字符
 */

/** 编译后的正则缓存，避免重复编译 */
const regexCache = new Map<string, RegExp>();

/** 缓存最大容量，防止无限增长导致内存泄漏 */
const REGEX_CACHE_MAX_SIZE = 200;

/**
 * 将 Ant 风格的路径模式转换为正则表达式（带缓存）
 */
function patternToRegex(pattern: string): RegExp {
  const cached = regexCache.get(pattern);
  if (cached) return cached;

  let regex = pattern
    // 转义正则特殊字符 (除了 *)
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
    // ** 匹配任意层级
    .replace(/\*\*/g, '{{GLOBSTAR}}')
    // * 匹配单层内任意字符 (不含 /)
    .replace(/\*/g, '[^/]*')
    // 还原 **
    .replace(/\{\{GLOBSTAR\}\}/g, '.*');

  if (!regex.startsWith('^')) {
    regex = `(^|/)${regex}`;
  }

  const compiled = new RegExp(regex);

  // LRU 式清理：超限时移除最早的一半条目，避免全量清空导致缓存命中率骤降
  if (regexCache.size >= REGEX_CACHE_MAX_SIZE) {
    const entriesToRemove = Math.floor(REGEX_CACHE_MAX_SIZE / 2);
    const keys = regexCache.keys();
    for (let i = 0; i < entriesToRemove; i++) {
      const next = keys.next();
      if (next.done) break;
      regexCache.delete(next.value);
    }
  }
  regexCache.set(pattern, compiled);

  return compiled;
}

/**
 * 从 URL 中提取路径部分（去除 query string 和 hash）
 */
function extractPath(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      return new URL(url).pathname;
    } catch {
      // URL 解析失败，使用字符串分割
    }
  }
  return url.split('?')[0].split('#')[0];
}

/**
 * 检查 URL 路径是否匹配排除模式列表
 *
 * @param url - 请求 URL (完整路径或相对路径)
 * @param patterns - 排除模式列表
 * @returns 是否应该被排除
 *
 * @example
 * ```ts
 * const excludePaths = ['/api/auth/**', '/public/*', '/health'];
 *
 * isExcludedPath('/api/auth/login', excludePaths)        // true
 * isExcludedPath('/api/auth/token/refresh', excludePaths) // true
 * isExcludedPath('/api/users', excludePaths)              // false
 * isExcludedPath('/health', excludePaths)                 // true
 * isExcludedPath('/health-check', excludePaths)           // false (段边界)
 * ```
 */
export function isExcludedPath(url: string, patterns: string[]): boolean {
  if (!url || !patterns?.length) return false;

  const path = extractPath(url);

  return patterns.some((pattern) => {
    // 精确匹配
    if (path === pattern) return true;

    // 包含通配符，使用正则匹配
    if (pattern.includes('*')) {
      return patternToRegex(pattern).test(path);
    }

    // 段边界前缀匹配 (防止 /health 匹配 /health-check)
    if (pattern.endsWith('/')) {
      return path.startsWith(pattern);
    }
    return path.startsWith(`${pattern}/`);
  });
}
